import * as React from 'react';

export function useSyncedHorizontalScroll({
  enabled,
  leftScrollRef,
  centerScrollRef,
  rightScrollRef,
  onScrollSync,
}: {
  enabled: boolean;
  leftScrollRef: React.RefObject<HTMLElement | null>;
  centerScrollRef: React.RefObject<HTMLElement | null>;
  rightScrollRef: React.RefObject<HTMLElement | null>;
  onScrollSync: () => void;
}) {
  React.useEffect(() => {
    const left = leftScrollRef.current;
    const center = centerScrollRef.current;
    const right = rightScrollRef.current;

    const syncing = { current: false };
    const syncXFrom = (src: 'left' | 'center' | 'right') => {
      if (!enabled) return;
      if (!left || !center || !right) return;
      if (syncing.current) return;
      syncing.current = true;
      try {
        const x = src === 'left' ? left.scrollLeft : src === 'right' ? right.scrollLeft : center.scrollLeft;
        if (src !== 'left') left.scrollLeft = x;
        if (src !== 'center') center.scrollLeft = x;
        if (src !== 'right') right.scrollLeft = x;
      } finally {
        syncing.current = false;
      }
    };

    const onLeftScroll = () => {
      syncXFrom('left');
      onScrollSync();
    };
    const onCenterScroll = () => {
      syncXFrom('center');
      onScrollSync();
    };
    const onRightScroll = () => {
      syncXFrom('right');
      onScrollSync();
    };

    left?.addEventListener('scroll', onLeftScroll, { passive: true });
    center?.addEventListener('scroll', onCenterScroll, { passive: true });
    right?.addEventListener('scroll', onRightScroll, { passive: true });
    return () => {
      left?.removeEventListener('scroll', onLeftScroll);
      center?.removeEventListener('scroll', onCenterScroll);
      right?.removeEventListener('scroll', onRightScroll);
    };
  }, [centerScrollRef, enabled, leftScrollRef, onScrollSync, rightScrollRef]);
}
